import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { MapPin, Pencil, Plus, Search, Trash2 } from "lucide-react";

import {
  AdminPage,
  Loading,
  useRows,
  type Row,
} from "@/components/admin/AdminKit";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export const Route = createFileRoute("/_authenticated/admin/continut")({
  component: ContentAdmin,
});

type BlockForm = {
  id?: string;
  key: string;
  title: string;
  body: string;
  sort_order: number;
};

const PLACEMENTS: Record<string, { page: string; where: string }> = {
  home_intro: { page: "Acasă", where: "Textul de introducere, imediat sub imaginea principală" },
  home_quote: { page: "Acasă", where: "Caseta evidențiată cu citatul, sub introducere" },
  teme_intro: { page: "Acasă & Tematică", where: "Textul de deasupra temelor de dezbatere" },
  partners_subtitle: { page: "Toate paginile", where: "Subtitlul de deasupra siglelor partenerilor" },
  tematica_intro: { page: "Tematică", where: "Textul principal al paginii" },
  speakeri_intro: { page: "Speakeri", where: "Textul de deasupra listei de speakeri" },
  speakeri_slogan: { page: "Speakeri", where: "Sloganul evidențiat din partea de sus" },
  agenda_intro: { page: "Agendă", where: "Textul de deasupra programului" },
  gala_intro: { page: "Gala Specialiștilor", where: "Textul de prezentare al galei" },
  parteneri_intro: { page: "Parteneri", where: "Textul de deasupra listei de parteneri" },
  contact_intro: { page: "Contact & Înregistrare", where: "Textul de deasupra formularului" },
};

function placementOf(key: string) {
  return (
    PLACEMENTS[key] ?? {
      page: "Bloc personalizat",
      where: "Nu este încă folosit într-o pagină",
    }
  );
}

function ContentAdmin() {
  const { rows, isLoading, save, remove } = useRows("content_blocks");
  const [editing, setEditing] = useState<BlockForm | null>(null);
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((item) => {
      const key = String(item["key"] ?? "").toLowerCase();
      const title = String(item["title"] ?? "").toLowerCase();
      const body = String(item["body"] ?? "").toLowerCase();
      const place = placementOf(String(item["key"] ?? ""));
      return (
        key.includes(q) ||
        title.includes(q) ||
        body.includes(q) ||
        place.page.toLowerCase().includes(q) ||
        place.where.toLowerCase().includes(q)
      );
    });
  }, [rows, query]);

  if (isLoading) return <Loading />;

  return (
    <AdminPage
      title="Conținut & texte"
      description="Fiecare card arată exact unde apare textul în site. Caută rapid după pagină, secțiune sau conținut."
      action={
        <Button
          onClick={() =>
            setEditing({
              key: `bloc-${Date.now()}`,
              title: "",
              body: "",
              sort_order: rows.length,
            })
          }
        >
          <Plus className="mr-2 h-4 w-4" /> Adaugă text
        </Button>
      }
    >
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Caută după pagină, secțiune, titlu sau conținut…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="pl-10"
        />
      </div>

      <div className="grid gap-4">
        {filtered.map((item: Row) => {
          const key = String(item["key"] ?? "");
          const place = placementOf(key);
          return (
            <Card key={item.id} className="p-5 transition-shadow hover:shadow-md">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-2.5 py-1 text-[11px] font-semibold text-primary">
                    <MapPin className="h-3 w-3" /> {place.page}
                  </span>
                  <p className="mt-2 font-semibold">{String(item["title"] ?? "Fără titlu")}</p>
                  <p className="text-xs text-muted-foreground">{place.where}</p>
                  {Boolean(item["body"]) && (
                    <p className="mt-2 line-clamp-2 text-sm text-muted-foreground">
                      {String(item["body"])}
                    </p>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() =>
                      setEditing({
                        id: item.id,
                        key,
                        title: String(item["title"] ?? ""),
                        body: String(item["body"] ?? ""),
                        sort_order: Number(item["sort_order"] ?? 0),
                      })
                    }
                  >
                    <Pencil className="mr-2 h-3.5 w-3.5" /> Editează
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    aria-label="Șterge"
                    onClick={() => {
                      if (window.confirm("Ștergi acest text din site?")) void remove(item.id);
                    }}
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              </div>
            </Card>
          );
        })}
        {filtered.length === 0 && (
          <p className="py-8 text-center text-sm text-muted-foreground">
            Nu s-a găsit niciun text pentru „{query}”.
          </p>
        )}
      </div>

      <Dialog open={editing !== null} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editing?.id ? "Editează textul" : "Text nou"}
            </DialogTitle>
            <DialogDescription>
              {editing
                ? `${placementOf(editing.key).page} — ${placementOf(editing.key).where}.`
                : ""}
            </DialogDescription>
          </DialogHeader>

          {editing && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Titlu afișat</Label>
                <Input
                  value={editing.title}
                  onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Text</Label>
                <Textarea
                  rows={8}
                  value={editing.body}
                  onChange={(e) => setEditing({ ...editing, body: e.target.value })}
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditing(null)}>
              Anulează
            </Button>
            <Button
              onClick={async () => {
                if (!editing) return;
                const ok = await save({ ...editing });
                if (ok) setEditing(null);
              }}
            >
              Salvează
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminPage>
  );
}
